import {
  GridDropdown,
  GridTextInput,
  ToggleCheckbox,
} from '../../utils/gridUtils';
import {
  DEFAULT_CAMERA_FAR,
  DEFAULT_CAMERA_FOV,
  DEFAULT_CAMERA_NEAR,
  applyCameraTransformFromProps,
  findCameraProp,
  getCameraLookupId,
  getCameraTransformVectors,
  getSceneCameraProjectionDefaults,
  hasPersistedSceneCameraProjection,
  isViewportCamera,
  replaceSceneCameraType,
  resetViewportCamera,
  resolveSceneCameraFar,
  resolveSceneCameraFov,
  switchCamera,
  updateCameraHelperFrustum,
} from '../gridpagefunction/Camera';
import { getCameraHelperVisible } from '../../utils/ThreeJsUtils/sceneCameraUtils';
import store from '../../redux/store';
import { selected_object } from '../../redux/Slice/gridSlice';
import { selectObjectInScene } from '../../pages/Dashboard/GridPage';

import {
  updateCameraType,
  cameraPropertiesUpdate,
} from '../../redux/Slice/gridSlice';
import * as Three from 'three';
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

/** Three.js PerspectiveCamera.fov is always vertical (degrees). */
const horizontalToVerticalFov = (horizontalDeg, aspect) => {
  const h = Three.MathUtils.degToRad(horizontalDeg);
  const v = 2 * Math.atan(Math.tan(h / 2) / aspect);
  return Three.MathUtils.radToDeg(v);
};

const normalizeFovAxis = (axis) => {
  if (!axis || typeof axis !== 'string') return 'Vertical';
  const lower = axis.toLowerCase();
  return lower === 'horizontal' ? 'Horizontal' : 'Vertical';
};

// Memo: re-render only when camera identity or key props change (avoids loop when parent re-renders)
const CameraComponent = React.memo(({ object, renderer, scene, controlsOrbit, historyUiRevision = 0 }) => {
  const dispatch = useDispatch();
  const {
    perspectiveCamera,
    orthographicCamera,
    currentCamera,
    update: updateCurrentCamera,
    showCameraHelper,
  } = object;

  const ensureCameraHelper = React.useCallback(
    (camera) => {
      if (!camera || !scene) return;
      if (typeof showCameraHelper === 'function') {
        showCameraHelper(true, camera, camera.name || camera.userData?.id);
      } else {
        updateCameraHelperFrustum(scene, camera);
      }
    },
    [scene, showCameraHelper]
  );

  // Get camera properties from Redux (assets_object)
  const { camera_properties } = useSelector((state) => state.assets_object);
  const { cameraType: reduxCameraType } = useSelector(
    (state) => state.assets_object
  );

  const initialCamProp = findCameraProp(
    camera_properties,
    getCameraLookupId(currentCamera)
  );
  const initialProjection = getSceneCameraProjectionDefaults(initialCamProp);

  // --- Use state for all UI-controlled values ---
  const [cameraType, setCameraType] = React.useState(
    currentCamera instanceof Three.PerspectiveCamera
      ? 'Perspective'
      : 'Orthographic'
  );
  const [near, setNear] = React.useState(() =>
    isViewportCamera(currentCamera, perspectiveCamera, orthographicCamera)
      ? currentCamera?.near ?? DEFAULT_CAMERA_NEAR
      : initialProjection.near
  );
  const [far, setFar] = React.useState(() =>
    isViewportCamera(currentCamera, perspectiveCamera, orthographicCamera)
      ? currentCamera?.far ?? DEFAULT_CAMERA_FAR
      : initialProjection.far
  );
  const [fov, setFov] = React.useState(() =>
    isViewportCamera(currentCamera, perspectiveCamera, orthographicCamera)
      ? currentCamera?.fov ?? DEFAULT_CAMERA_FOV
      : initialProjection.fov
  );
  const [fovAxis, setFovAxis] = React.useState(() =>
    normalizeFovAxis(initialCamProp?.fov_axis)
  );
  const [orthoSize, setOrthoSize] = React.useState(() => {
    if (currentCamera instanceof Three.OrthographicCamera) {
      return Math.abs(currentCamera.top - currentCamera.bottom) || 5;
    }
    return 5;
  });
  const [helperVisible, setHelperVisible] = React.useState(() =>
    getCameraHelperVisible(initialCamProp)
  );
  const [resetInputNonce, setResetInputNonce] = React.useState(0);

  const isViewCamera = isViewportCamera(
    currentCamera,
    perspectiveCamera,
    orthographicCamera
  );
  React.useEffect(() => {
    if (!isViewCamera || !reduxCameraType) return;
    // Convert Redux lowercase to component capitalized format
    const capitalizedType =
      reduxCameraType.charAt(0).toUpperCase() + reduxCameraType.slice(1);

    // Only update if different from current state
    if (capitalizedType !== cameraType) {
      setCameraType(capitalizedType);

      // Switch the actual camera
      const newCamera = switchCamera(
        capitalizedType,
        perspectiveCamera,
        orthographicCamera,
        currentCamera,
        renderer,
        scene,
        controlsOrbit,
        updateCurrentCamera
      );

      if (typeof updateCurrentCamera === 'function') {
        updateCurrentCamera(newCamera);
      }
    }
  }, [reduxCameraType, isViewCamera]);

  React.useEffect(() => {
    if (!isViewCamera) return;
    const lowercaseType = cameraType.toLowerCase();
    if (reduxCameraType !== lowercaseType) {
      dispatch(updateCameraType(lowercaseType));
    }
  }, [cameraType, reduxCameraType, dispatch, isViewCamera]);

  // Reset panel fields when a different camera is selected (Three.js camera is source of truth for fov).
  React.useEffect(() => {
    if (!currentCamera) return;
    setCameraType(
      currentCamera instanceof Three.PerspectiveCamera
        ? 'Perspective'
        : 'Orthographic'
    );
    const lookupId = getCameraLookupId(currentCamera);
    const camProp = findCameraProp(camera_properties, lookupId);
    const viewCam = isViewportCamera(
      currentCamera,
      perspectiveCamera,
      orthographicCamera
    );
    const projectionDefaults = getSceneCameraProjectionDefaults(camProp);
    const resolvedNear = viewCam
      ? currentCamera.near ?? DEFAULT_CAMERA_NEAR
      : projectionDefaults.near;
    const resolvedFar = viewCam
      ? currentCamera.far ?? DEFAULT_CAMERA_FAR
      : projectionDefaults.far;
    setNear(resolvedNear);
    setFar(resolvedFar);
    if (!viewCam && currentCamera.near !== resolvedNear) {
      currentCamera.near = resolvedNear;
    }
    if (!viewCam && currentCamera.far !== resolvedFar) {
      currentCamera.far = resolvedFar;
      currentCamera.updateProjectionMatrix();
      updateCameraHelperFrustum(scene, currentCamera);
    }
    if (currentCamera.isPerspectiveCamera) {
      const resolvedFov = viewCam
        ? currentCamera.fov ?? DEFAULT_CAMERA_FOV
        : projectionDefaults.fov;
      setFov(resolvedFov);
      if (!viewCam && currentCamera.fov !== resolvedFov) {
        currentCamera.fov = resolvedFov;
        currentCamera.updateProjectionMatrix();
        updateCameraHelperFrustum(scene, currentCamera);
      }
    }
    if (currentCamera.isOrthographicCamera) {
      setOrthoSize(Math.abs(currentCamera.top - currentCamera.bottom) || 5);
    }
    setFovAxis(normalizeFovAxis(camProp?.fov_axis));
    if (!viewCam) {
      setHelperVisible(getCameraHelperVisible(camProp));
    }

    if (!viewCam && lookupId) {
      applyCameraTransformFromProps(currentCamera, camProp);
      const transform = getCameraTransformVectors(currentCamera);
      const projectionSeed = getSceneCameraProjectionDefaults(camProp);
      const shouldSeedProjection = !hasPersistedSceneCameraProjection(camProp);

      if (transform || shouldSeedProjection) {
        const rp = camProp?.position;
        const rr = camProp?.rotation;
        const posChanged =
          !rp ||
          Math.abs((rp.x ?? 0) - transform.position.x) > 1e-6 ||
          Math.abs((rp.y ?? 0) - transform.position.y) > 1e-6 ||
          Math.abs((rp.z ?? 0) - transform.position.z) > 1e-6;
        const rotChanged =
          !rr ||
          Math.abs((rr.x ?? 0) - transform.rotation.x) > 1e-6 ||
          Math.abs((rr.y ?? 0) - transform.rotation.y) > 1e-6 ||
          Math.abs((rr.z ?? 0) - transform.rotation.z) > 1e-6;

        if (posChanged || rotChanged || shouldSeedProjection) {
          dispatch(
            cameraPropertiesUpdate({
              _id: lookupId,
              id: lookupId,
              type:
                camProp?.type ||
                (currentCamera instanceof Three.PerspectiveCamera
                  ? 'Perspective'
                  : 'Orthographic'),
              ...(transform || {}),
              ...(shouldSeedProjection
                ? {
                    near: projectionSeed.near,
                    far: projectionSeed.far,
                    ...(currentCamera.isPerspectiveCamera
                      ? { fov: projectionSeed.fov, fov_axis: 'vertical' }
                      : {}),
                    projectionEdited: false,
                  }
                : {}),
            })
          );
        }
      }
      updateCameraHelperFrustum(scene, currentCamera);
    }
  }, [
    currentCamera?.uuid,
    camera_properties,
    perspectiveCamera,
    orthographicCamera,
    scene,
    dispatch,
    historyUiRevision,
  ]);

  // --- Sync fovAxis from Redux for the selected camera only (never viewport camera_properties[0]) ---
  React.useEffect(() => {
    if (!currentCamera) return;
    const cam = findCameraProp(
      camera_properties,
      getCameraLookupId(currentCamera)
    );
    if (!cam) return;
    const normalized = normalizeFovAxis(cam.fov_axis);
    setFovAxis((prev) => (prev !== normalized ? normalized : prev));
  }, [
    camera_properties,
    currentCamera?.name,
    currentCamera?.userData?.id,
    currentCamera?.uuid,
  ]);

  // --- Sync from Redux camera_properties when they change ---
  // Find the camera in Redux that matches the current camera we're editing (by _id or name)
  React.useEffect(() => {
    if (
      !camera_properties ||
      !Array.isArray(camera_properties) ||
      camera_properties.length === 0
    ) {
      return;
    }

    const lookupId = getCameraLookupId(currentCamera);
    const camProp = lookupId
      ? findCameraProp(camera_properties, lookupId)
      : isViewCamera
        ? camera_properties[0]
        : null;
    const hasMatch = Boolean(camProp && lookupId);

    if (!camProp) {
      return;
    }

    const trustCamPropType = isViewCamera || hasMatch;

    const needsTypeSwitch =
      trustCamPropType &&
      camProp.type &&
      camProp.type !==
      (currentCamera instanceof Three.PerspectiveCamera
        ? 'Perspective'
        : 'Orthographic');
    const typeMatches =
      !needsTypeSwitch &&
      (!camProp.type ||
        camProp.type ===
        (currentCamera instanceof Three.PerspectiveCamera
          ? 'Perspective'
          : 'Orthographic'));
    const numEq = (a, b) =>
      typeof a === 'number' &&
      typeof b === 'number' &&
      (a === b ||
        (Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) < 1e-10));
    const sizeEq = (a, b) =>
      a == null || b == null
        ? a === b
        : Math.abs(parseFloat(a) - parseFloat(b)) < 1e-10;
    const sceneProjection = !isViewCamera
      ? getSceneCameraProjectionDefaults(camProp)
      : null;
    const effectiveNear = isViewCamera ? camProp.near : sceneProjection?.near;
    const effectiveFar = isViewCamera ? camProp.far : sceneProjection?.far;
    const effectiveFov = isViewCamera ? camProp.fov : sceneProjection?.fov;

    const valuesMatch =
      (typeof effectiveNear !== 'number' || numEq(effectiveNear, near)) &&
      (typeof effectiveFar !== 'number' || numEq(effectiveFar, far)) &&
      (typeof effectiveFov !== 'number' || numEq(effectiveFov, fov)) &&
      (typeof camProp.fov_axis !== 'string' ||
        camProp.fov_axis === fovAxis.toLowerCase()) &&
      (camProp.size == null || camProp.size === '' || sizeEq(camProp.size, orthoSize));

    // If Redux already matches local state and no type switch, only update Three.js and skip setState to avoid loop
    if (typeMatches && valuesMatch && !needsTypeSwitch) {
      if (typeof effectiveNear === 'number') currentCamera.near = effectiveNear;
      if (typeof effectiveFar === 'number') currentCamera.far = effectiveFar;
      if (currentCamera.isPerspectiveCamera && typeof effectiveFov === 'number') {
        currentCamera.fov = effectiveFov;
        currentCamera.updateProjectionMatrix();
      }
      if (currentCamera.isOrthographicCamera && camProp.size) {
        const size = parseFloat(camProp.size);
        if (!isNaN(size)) {
          const aspect = window.innerWidth / window.innerHeight || 1;
          currentCamera.top = size / 2;
          currentCamera.bottom = -size / 2;
          currentCamera.right = (size * aspect) / 2;
          currentCamera.left = -(size * aspect) / 2;
          currentCamera.updateProjectionMatrix();
        }
      }
      if (isViewCamera && renderer && typeof renderer.render === 'function') {
        renderer.render(scene, currentCamera);
      }
      return;
    }

    if (trustCamPropType && camProp.type && camProp.type !== cameraType) {
      setCameraType(camProp.type);
    }
    if (typeof effectiveNear === 'number' && effectiveNear !== near) {
      setNear(effectiveNear);
    } else if (
      !isViewCamera &&
      camProp.near == null &&
      near !== DEFAULT_CAMERA_NEAR
    ) {
      setNear(DEFAULT_CAMERA_NEAR);
    }
    if (isViewCamera) {
      if (typeof effectiveFar === 'number' && effectiveFar !== far) {
        setFar(effectiveFar);
      }
    } else {
      const resolvedFar = resolveSceneCameraFar(
        currentCamera.far,
        camProp.far,
        camProp
      );
      if (resolvedFar !== far) {
        setFar(resolvedFar);
      }
      if (currentCamera.far !== resolvedFar) {
        currentCamera.far = resolvedFar;
        currentCamera.updateProjectionMatrix();
      }
    }
    if (currentCamera.isPerspectiveCamera) {
      if (isViewCamera) {
        if (currentCamera.fov !== fov) {
          setFov(currentCamera.fov);
        }
      } else {
        const resolvedFov = resolveSceneCameraFov(
          currentCamera.fov,
          camProp.fov,
          camProp
        );
        if (resolvedFov !== fov) {
          setFov(resolvedFov);
        }
        if (currentCamera.fov !== resolvedFov) {
          currentCamera.fov = resolvedFov;
          currentCamera.updateProjectionMatrix();
        }
      }
    } else if (typeof camProp.fov === 'number' && camProp.fov !== fov) {
      setFov(camProp.fov);
    }
    const normalizedAxis = normalizeFovAxis(camProp.fov_axis);
    if (normalizedAxis !== fovAxis) {
      setFovAxis(normalizedAxis);
    }
    if (camProp.size && camProp.size !== orthoSize) {
      let size = parseFloat(camProp.size);
      if (Number.isFinite(size)) {
        setOrthoSize(size);
      }
    }

    let activeCamera = currentCamera;
    const currentTypeLabel =
      currentCamera instanceof Three.PerspectiveCamera
        ? 'Perspective'
        : 'Orthographic';
    if (trustCamPropType && camProp.type && camProp.type !== currentTypeLabel) {
      if (isViewCamera) {
        activeCamera = switchCamera(
          camProp.type,
          perspectiveCamera,
          orthographicCamera,
          currentCamera,
          renderer,
          scene,
          controlsOrbit,
          updateCurrentCamera
        );
        dispatch(updateCameraType(camProp.type.toLowerCase()));
        if (typeof updateCurrentCamera === 'function') {
          updateCurrentCamera(activeCamera);
        }
      } else if (
        (camProp.type === 'Perspective' && !currentCamera.isPerspectiveCamera) ||
        (camProp.type === 'Orthographic' && !currentCamera.isOrthographicCamera)
      ) {
        activeCamera = replaceSceneCameraType(
          currentCamera,
          camProp.type,
          scene,
          {
            near: sceneProjection?.near ?? camProp.near,
            far: sceneProjection?.far ?? camProp.far,
            fov: sceneProjection?.fov ?? camProp.fov,
            orthoSize: camProp.size ? parseFloat(camProp.size) : orthoSize,
          }
        );
        store.dispatch(selected_object(activeCamera));
        selectObjectInScene(activeCamera.name);
      }
    }

    const editCamera = activeCamera;
    const editProjection = !isViewCamera
      ? getSceneCameraProjectionDefaults(camProp)
      : null;
    if (typeof (editProjection?.near ?? camProp.near) === 'number') {
      editCamera.near = editProjection?.near ?? camProp.near;
    }
    if (typeof (editProjection?.far ?? camProp.far) === 'number') {
      editCamera.far = editProjection?.far ?? camProp.far;
    }
    if (
      editCamera.isPerspectiveCamera &&
      typeof (editProjection?.fov ?? camProp.fov) === 'number'
    ) {
      editCamera.fov = editProjection?.fov ?? camProp.fov;
      editCamera.updateProjectionMatrix();
    }
    if (editCamera.isOrthographicCamera && camProp.size) {
      let size = parseFloat(camProp.size);
      if (isNaN(size)) size = 5;
      const aspect = window.innerWidth / window.innerHeight || 1;
      editCamera.top = size / 2;
      editCamera.bottom = -size / 2;
      editCamera.right = (size * aspect) / 2;
      editCamera.left = -(size * aspect) / 2;
      editCamera.updateProjectionMatrix();
    }

    if (isViewCamera) {
      if (typeof updateCurrentCamera === 'function') {
        updateCurrentCamera(activeCamera);
      }
      window.currentCamera = activeCamera;
      window.camera = activeCamera;
      window.dispatchEvent(
        new CustomEvent('cameraUpdated', { detail: activeCamera })
      );
      if (renderer && typeof renderer.render === 'function') {
        renderer.render(scene, activeCamera);
      }
      if (controlsOrbit && typeof controlsOrbit.update === 'function') {
        controlsOrbit.object = activeCamera;
        controlsOrbit.update();
      }
    }
  }, [
    camera_properties,
    currentCamera?.name,
    currentCamera?.uuid,
    perspectiveCamera,
    orthographicCamera,
  ]);

  // --- Keep Three.js camera in sync with UI state (projection only; do not switch view on every change) ---
  // Apply to selected camera, refresh CameraHelper frustum, and trigger a render.
  React.useEffect(() => {
    if (!currentCamera) return;
    currentCamera.near = near;
    currentCamera.far = far;
    if (currentCamera.isPerspectiveCamera) {
      currentCamera.fov = fov;
      currentCamera.updateProjectionMatrix();
    } else if (currentCamera.isOrthographicCamera) {
      const aspect = window.innerWidth / window.innerHeight || 1;
      currentCamera.top = orthoSize / 2;
      currentCamera.bottom = -orthoSize / 2;
      currentCamera.right = (orthoSize * aspect) / 2;
      currentCamera.left = -(orthoSize * aspect) / 2;
      currentCamera.updateProjectionMatrix();
    }
    if (!isViewCamera) {
      updateCameraHelperFrustum(scene, currentCamera);
    }
    const viewCamera = window.currentCamera || perspectiveCamera;
    if (renderer && scene && viewCamera && typeof renderer.render === 'function') {
      renderer.render(scene, viewCamera);
    }
    // Do not call updateCurrentCamera here — it would re-run on every near/far/fov change and can break the grid. View switch happens only in the Redux sync effect when we first select the camera or change type.
  }, [
    near,
    far,
    fov,
    orthoSize,
    cameraType,
    currentCamera?.uuid,
    renderer,
    scene,
    perspectiveCamera,
    isViewCamera,
  ]);

  // FOV axis dropdown is hidden — always show vertical FOV (matches Three.js camera.fov).
  const displayFov = fov;

  // Update Redux camera properties (pass single object with _id so reducer updates the correct camera)
  const updateReduxCameraProperties = React.useCallback(
    (overrides = {}) => {
      const payload = {
        _id: getCameraLookupId(currentCamera),
        id: getCameraLookupId(currentCamera),
        type: overrides.type || cameraType,
        near: Number(overrides.near !== undefined ? overrides.near : near),
        far: Number(overrides.far !== undefined ? overrides.far : far),
        fov:
          (overrides.type || cameraType) === 'Perspective'
            ? Number(overrides.fov !== undefined ? overrides.fov : fov)
            : undefined,
        fov_axis:
          (overrides.type || cameraType) === 'Perspective'
            ? (overrides.fov_axis || 'vertical')
            : undefined,
        size:
          (overrides.type || cameraType) === 'Orthographic'
            ? overrides.size !== undefined
              ? overrides.size
              : orthoSize
            : undefined,
        position: {
          x: currentCamera?.position.x || 0,
          y: currentCamera?.position.y || 0,
          z: currentCamera?.position.z || 0,
        },
        rotation: {
          x: currentCamera?.rotation.x || 0,
          y: currentCamera?.rotation.y || 0,
          z: currentCamera?.rotation.z || 0,
        },
        ...(!isViewCamera
          ? {
              helperVisible:
                overrides.helperVisible !== undefined
                  ? overrides.helperVisible === true
                  : helperVisible === true,
            }
          : {}),
        ...(overrides.near !== undefined ||
        overrides.far !== undefined ||
        overrides.fov !== undefined
          ? { projectionEdited: true }
          : {}),
        ...(overrides.projectionEdited !== undefined
          ? { projectionEdited: overrides.projectionEdited }
          : {}),
      };

      dispatch(cameraPropertiesUpdate(payload));

      // Also update global window property for backward compatibility
      window.cameraProperties = [payload];
    },
    [
      cameraType,
      near,
      far,
      fov,
      orthoSize,
      helperVisible,
      isViewCamera,
      dispatch,
      currentCamera,
    ]
  );

  const handleHelperVisibleToggle = React.useCallback(() => {
    const next = !helperVisible;
    setHelperVisible(next);
    updateReduxCameraProperties({ helperVisible: next });

    if (typeof showCameraHelper !== 'function' || !currentCamera) return;

    const lookupId = getCameraLookupId(currentCamera);
    const selectedCamera = store.getState()?.assets_object?.select_object;
    const isSelected =
      selectedCamera?.name === currentCamera.name ||
      selectedCamera?.uuid === currentCamera.uuid;

    if (next || isSelected) {
      showCameraHelper(true, currentCamera, lookupId);
    } else {
      // Hide only this camera's helper — leave other helperVisible cameras alone.
      showCameraHelper(false, currentCamera, lookupId);
    }
  }, [
    helperVisible,
    currentCamera,
    showCameraHelper,
    updateReduxCameraProperties,
  ]);

  const applyFovInputToCamera = React.useCallback(
    (inputDeg) => {
      if (!currentCamera?.isPerspectiveCamera || !Number.isFinite(inputDeg)) {
        return;
      }
      const aspect =
        currentCamera.aspect || window.innerWidth / window.innerHeight || 1;
      const verticalFov =
        fovAxis === 'Horizontal'
          ? horizontalToVerticalFov(inputDeg, aspect)
          : inputDeg;
      setFov(verticalFov);
      currentCamera.fov = verticalFov;
      currentCamera.updateProjectionMatrix();
      updateReduxCameraProperties({ fov: verticalFov, fov_axis: 'vertical' });
      if (!isViewCamera) {
        updateCameraHelperFrustum(scene, currentCamera);
      }
      const viewCamera = window.currentCamera || perspectiveCamera;
      if (renderer && scene && viewCamera) {
        renderer.render(scene, viewCamera);
      }
    },
    [
      currentCamera,
      fovAxis,
      isViewCamera,
      perspectiveCamera,
      renderer,
      scene,
      updateReduxCameraProperties,
    ]
  );

  // Apply property overrides to the selected Three.js camera, update the camera helper so the frustum reflects changes, and re-render.
  const applyToCameraAndRender = React.useCallback(
    (overrides) => {
      if (!currentCamera || !renderer || !scene) return;
      const n = overrides.near !== undefined ? overrides.near : near;
      const f = overrides.far !== undefined ? overrides.far : far;
      const fv = overrides.fov !== undefined ? overrides.fov : fov;
      const sz = overrides.orthoSize !== undefined ? overrides.orthoSize : orthoSize;
      if (typeof n === 'number') currentCamera.near = n;
      if (typeof f === 'number') currentCamera.far = f;
      if (currentCamera.isPerspectiveCamera && typeof fv === 'number') {
        currentCamera.fov = fv;
        currentCamera.updateProjectionMatrix();
      } else if (currentCamera.isOrthographicCamera) {
        const aspect = window.innerWidth / window.innerHeight || 1;
        const s = typeof sz === 'number' && Number.isFinite(sz) ? sz : 5;
        currentCamera.top = s / 2;
        currentCamera.bottom = -s / 2;
        currentCamera.right = (s * aspect) / 2;
        currentCamera.left = -(s * aspect) / 2;
        currentCamera.updateProjectionMatrix();
      }
      if (!isViewCamera) {
        updateCameraHelperFrustum(scene, currentCamera);
      }
      const viewCamera = window.currentCamera || perspectiveCamera;
      if (viewCamera && typeof renderer.render === 'function') {
        renderer.render(scene, viewCamera);
      }
    },
    [
      currentCamera,
      near,
      far,
      fov,
      orthoSize,
      renderer,
      scene,
      perspectiveCamera,
      isViewCamera,
    ]
  );

  // Helper for drag-to-update
  const handleDragValue = (
    initialValue,
    setLocalValue,
    onChange,
    options = {}
  ) => {
    const {
      min = -Infinity,
      max = Infinity,
      sensitivity = 1,
      precision = 2,
      property = null,
    } = options;
    let startX = null;
    let startValue = null;

    const onMouseMove = (e) => {
      if (startX === null) return;
      const deltaPx = e.clientX - startX;
      let newValue = startValue + deltaPx * sensitivity;
      newValue = Math.max(min, Math.min(max, newValue));
      newValue = parseFloat(newValue.toFixed(precision));
      setLocalValue(newValue);
      if (onChange) onChange(newValue);

      // Update Redux immediately during drag if property is specified
      if (property) {
        const overrides = {};
        overrides[property] = newValue;
        updateReduxCameraProperties(overrides);
      }
    };

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      startX = null;
      startValue = null;
    };

    return (e) => {
      e.preventDefault();
      startX = e.clientX;
      startValue = initialValue;
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    };
  };

  // Update FOV axis
  const handleFovAxisChange = (e) => {
    const axis = e.target.value;
    setFovAxis(axis);
    // Update Redux immediately
    updateReduxCameraProperties({ fov_axis: axis });
  };

  // Update orthographic size

  const handleCameraTypeChange = (e) => {
    const value = e.target.value;
    setCameraType(value);
    setFovAxis('Vertical');

    if (isViewCamera) {
      const newCamera = switchCamera(
        value,
        perspectiveCamera,
        orthographicCamera,
        currentCamera,
        renderer,
        scene,
        controlsOrbit,
        updateCurrentCamera
      );
      dispatch(updateCameraType(value.toLowerCase()));
      if (typeof updateCurrentCamera === 'function') {
        updateCurrentCamera(newCamera);
      }
    } else {
      const newCamera = replaceSceneCameraType(currentCamera, value, scene, {
        near,
        far,
        fov,
        orthoSize: value === 'Orthographic' ? orthoSize : 5,
      });
      store.dispatch(selected_object(newCamera));
      selectObjectInScene(newCamera.name);
      ensureCameraHelper(newCamera);
    }

    if (value === 'Orthographic') {
      setOrthoSize(5);
      updateReduxCameraProperties({
        type: value,
        size: 5,
        fov_axis: 'vertical',
      });
    } else {
      updateReduxCameraProperties({ type: value, fov_axis: 'vertical' });
    }

    const viewCamera = window.currentCamera || perspectiveCamera;
    if (renderer && scene && viewCamera) {
      renderer.render(scene, viewCamera);
    }
  };

  const handleReset = () => {
    if (document?.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }

    const defaultType = 'Perspective';
    const defaultNear = DEFAULT_CAMERA_NEAR;
    const defaultFar = DEFAULT_CAMERA_FAR;
    const defaultFov = DEFAULT_CAMERA_FOV;
    const defaultFovAxis = 'Vertical';
    const defaultOrthoSize = 5;

    let activeCamera = currentCamera;

    if (isViewCamera) {
      resetViewportCamera(
        activeCamera,
        defaultType,
        renderer,
        scene,
        controlsOrbit
      );
      dispatch(updateCameraType(defaultType.toLowerCase()));
    } else if (activeCamera && scene) {
      // Scene camera: reset projection only — never move position or orbit target
      if (
        activeCamera.isOrthographicCamera ||
        cameraType === 'Orthographic'
      ) {
        activeCamera = replaceSceneCameraType(activeCamera, defaultType, scene, {
          near: defaultNear,
          far: defaultFar,
          fov: defaultFov,
        });
        store.dispatch(selected_object(activeCamera));
        selectObjectInScene(activeCamera.name);
      } else {
        activeCamera.near = defaultNear;
        activeCamera.far = defaultFar;
        activeCamera.fov = defaultFov;
        activeCamera.updateProjectionMatrix();
      }
      updateCameraHelperFrustum(scene, activeCamera);
    }

    setCameraType(defaultType);
    setNear(defaultNear);
    setFar(defaultFar);
    setFov(defaultFov);
    setFovAxis(defaultFovAxis);
    setOrthoSize(defaultOrthoSize);
    setResetInputNonce((prev) => prev + 1);

    updateReduxCameraProperties({
      type: defaultType,
      near: defaultNear,
      far: defaultFar,
      fov: defaultFov,
      fov_axis: defaultFovAxis.toLowerCase(),
      size: defaultOrthoSize,
      projectionEdited: false,
    });

    const viewCamera = window.currentCamera || perspectiveCamera;
    if (renderer && scene && viewCamera) {
      renderer.render(scene, viewCamera);
    }
  };

  // Do NOT run an effect that pushes near/far/fov/etc. to Redux on every state change —
  // it causes: setState → effect runs → dispatch → sync effect runs → loop.
  // Redux is updated only from user actions (onChange, handleCameraTypeChange, handleReset, etc.).

  return (
    <div className="text-xs flex flex-col gap-1 ">
      <GridDropdown
        label="Type"
        value={cameraType}
        options={[
          { value: 'Perspective', label: 'Perspective' },
          { value: 'Orthographic', label: 'Orthographic' },
        ]}
        onChange={handleCameraTypeChange}
      />

      {/* Perspective FOV axis dropdown */}
      {/* {cameraType === 'Perspective' && (
        <div className="text-xs flex flex-col gap-1">
          <GridDropdown
            label="FOV Axis"
            value={fovAxis}
            options={[
              { value: 'Vertical', label: 'Vertical' },
              { value: 'Horizontal', label: 'Horizontal' },
            ]}
            onChange={handleFovAxisChange}
          />
        </div>
      )} */}

      <div className="flex items-center gap-2 w-full">
        <GridTextInput
          key={`camera-near-${currentCamera?.uuid || 'unknown'}-${resetInputNonce}`}
          flag={true}
          label="NEAR"
          value={near}
          debounceMs={0}
          onChange={(e) => {
            const input = e.target.value;
            const validNumberPattern = /^(\d+(\.\d+)?|\.\d+)?$/;
            if (input === '' || validNumberPattern.test(input)) {
              const numVal = input === '' ? '' : parseFloat(input);
              setNear(numVal);
              updateReduxCameraProperties({ near: numVal });
              if (typeof numVal === 'number') applyToCameraAndRender({ near: numVal });
            } else {
              e.target.value = near.toString();
            }
          }}
          type="number"
          className="w-full"
          startAdornment={(setLocalValue) => (
            <span
              className={`mr-2 text-gray-400 text-[10px] cursor-ew-resize select-none ${setLocalValue ? 'min-w-[73px]' : ''} ${setLocalValue ? '' : 'bg-[#222428]'} px-1 rounded`}
              style={{ pointerEvents: 'auto' }}
              onMouseDown={handleDragValue(
                near,
                setNear,
                (v) => {
                  setNear(v);
                  updateReduxCameraProperties({ near: v });
                  applyToCameraAndRender({ near: v });
                },
                { min: 0.01, max: far - 0.01, sensitivity: 0.05, precision: 3 }
              )}
              title="Drag to adjust Near"
            >
              {setLocalValue || 'N'}
            </span>
          )}
        />
      </div>

      <div className="flex items-center gap-2 w-full">
        <GridTextInput
          key={`camera-far-${currentCamera?.uuid || 'unknown'}-${resetInputNonce}`}
          flag={true}
          label="FAR"
          value={far}
          debounceMs={0}
          onChange={(e) => {
            const input = e.target.value;
            const validNumberPattern = /^(\d+(\.\d+)?|\.\d+)?$/;
            if (input === '' || validNumberPattern.test(input)) {
              const numVal = input === '' ? '' : parseFloat(input);
              setFar(numVal);
              updateReduxCameraProperties({ far: numVal });
              if (typeof numVal === 'number') applyToCameraAndRender({ far: numVal });
            } else {
              e.target.value = far.toString();
            }
          }}
          type="number"
          className="w-full"
          endAdornment={(setLocalValue) => null}
          startAdornment={(setLocalValue) => (
            <span
              className={`mr-2 text-gray-400 text-[10px] cursor-ew-resize select-none ${setLocalValue ? 'min-w-[73px]' : ''} ${setLocalValue ? '' : 'bg-[#222428]'} px-1 rounded`}
              style={{ pointerEvents: 'auto' }}
              onMouseDown={handleDragValue(
                far,
                setFar,
                (v) => {
                  setFar(v);
                  updateReduxCameraProperties({ far: v });
                  applyToCameraAndRender({ far: v });
                },
                { min: near + 0.01, sensitivity: 1 }
              )}
              title="Drag to adjust Far"
            >
              {setLocalValue || 'F'}
            </span>
          )}
        />
      </div>

      {/* Perspective FOV input */}
      {cameraType === 'Perspective' && (
        <div className="flex items-center gap-2 w-full">
          <GridTextInput
            key={`camera-fov-${currentCamera?.uuid || 'unknown'}-${resetInputNonce}`}
            flag={true}
            label={`FOV`}
            value={displayFov}
            debounceMs={0}
            onChange={(e) => {
              const input = e.target.value;
              const validNumberPattern = /^(\d+(\.\d+)?|\.\d+)?$/;
              if (input === '' || validNumberPattern.test(input)) {
                const numVal = input === '' ? '' : parseFloat(input);
                if (typeof numVal === 'number') {
                  applyFovInputToCamera(numVal);
                } else {
                  setFov(numVal);
                }
              } else {
                e.target.value = displayFov.toString();
              }
            }}
            type="number"
            className="w-full"
            endAdornment={(setLocalValue) => null}
            startAdornment={(setLocalValue) => (
              <span
                className={`mr-2 text-gray-400 text-[10px] cursor-ew-resize select-none ${setLocalValue ? 'min-w-[73px]' : ''} ${setLocalValue ? '' : 'bg-[#222428]'} px-1 rounded`}
                style={{ pointerEvents: 'auto' }}
                onMouseDown={handleDragValue(
                  displayFov,
                  setFov,
                  (v) => applyFovInputToCamera(v),
                  { min: 10, max: 120, sensitivity: 0.5 }
                )}
                title="Drag to adjust Fov"
              >
                {setLocalValue || 'F'}
              </span>
            )}
          />
        </div>
      )}

      {/* Orthographic size input */}
      {cameraType === 'Orthographic' && (
        <div className="flex items-center gap-2 w-full">
          <GridTextInput
            key={`camera-size-${currentCamera?.uuid || 'unknown'}-${resetInputNonce}`}
            flag={true}
            label="SIZE"
            value={orthoSize === Infinity ? 'Infinity' : orthoSize}
            debounceMs={0}
            onChange={(e) => {
              const input = e.target.value;
              const validNumberPattern = /^(\d+(\.\d+)?|\.\d+)?$/;
              if (
                input === '' ||
                input === 'Infinity' ||
                validNumberPattern.test(input)
              ) {
                let newSize =
                  input === ''
                    ? ''
                    : input === 'Infinity'
                      ? Infinity
                      : parseFloat(input);
                setOrthoSize(newSize);
                updateReduxCameraProperties({ size: newSize });
                if (typeof newSize === 'number' && Number.isFinite(newSize))
                  applyToCameraAndRender({ orthoSize: newSize });
              } else {
                e.target.value = orthoSize.toString();
              }
            }}
            type="number"
            className="w-full"
            endAdornment={(setLocalValue) => null}
            startAdornment={(setLocalValue) => (
              <span
                className={`mr-2 text-gray-400 text-[10px] cursor-ew-resize select-none ${setLocalValue ? 'min-w-[73px]' : ''} ${setLocalValue ? '' : 'bg-[#222428]'} px-1 rounded`}
                style={{ pointerEvents: 'auto' }}
                onMouseDown={handleDragValue(
                  orthoSize,
                  setOrthoSize,
                  (v) => {
                    setOrthoSize(v);
                    updateReduxCameraProperties({ size: v });
                    applyToCameraAndRender({ orthoSize: v });
                  },
                  { min: 0.01, sensitivity: 0.5 }
                )}
                title="Drag to adjust Size"
              >
                {setLocalValue || 'S'}
              </span>
            )}
          />
        </div>
      )}

      {!isViewCamera && (
        <ToggleCheckbox
          label="Camera Helper Visible"
          checked={helperVisible}
          onToggle={handleHelperVisibleToggle}
        />
      )}

      <button
        className=" bg-[#2B2C2F] text-white py-2 px-2 rounded"
        onClick={handleReset}
      >
        Reset
      </button>
    </div>
  );
});

export default CameraComponent;
